import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { SearchableSelect } from '../components/ui/searchable-select';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  ArrowLeft,
  Loader2,
  Users,
  Calendar,
  CheckSquare,
  ExternalLink,
  Download,
  Filter,
  Languages
} from 'lucide-react';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

export default function JiraTasksPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [issues, setIssues] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [filterType, setFilterType] = useState('assigned');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState(new Set());
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/jira/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch Jira users:', error);
      toast.error('Failed to load Jira users. Check configuration.');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchIssues = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    setLoadingIssues(true);
    setSelectedIssues(new Set());
    
    try {
      const response = await api.get('/jira/issues', {
        params: {
          user_id: selectedUserId,
          year: selectedYear,
          month: selectedMonth,
          filter_type: filterType
        }
      });
      setIssues(response.data.issues || []);
      if (response.data.issues?.length === 0) {
        toast.info('No issues found for this period');
      } else {
        const filterLabel = filterType === 'assigned' ? 'assigned to' : 'reported by';
        toast.success(`Found ${response.data.total} issues ${filterLabel} user`);
      }
    } catch (error) {
      console.error('Failed to fetch issues:', error);
      toast.error('Failed to load issues from Jira');
    } finally {
      setLoadingIssues(false);
    }
  };

  const translateToEnglish = async () => {
    if (issues.length === 0) {
      toast.error('No tasks to translate');
      return;
    }

    setTranslating(true);
    try {
      const textsToTranslate = issues.map(i => i.subject);
      const response = await api.post('/translate-to-english', {
        texts: textsToTranslate
      });
      
      const translations = response.data.translations || [];
      
      const translatedIssues = issues.map((issue, index) => ({
        ...issue,
        subject: translations[index] || issue.subject
      }));
      
      setIssues(translatedIssues);
      toast.success(`Translated ${translations.length} tasks to English`);
    } catch (error) {
      console.error('Translation failed:', error);
      toast.error('Failed to translate tasks');
    } finally {
      setTranslating(false);
    }
  };

  const toggleIssueSelection = (issueId) => {
    const newSelected = new Set(selectedIssues);
    if (newSelected.has(issueId)) {
      newSelected.delete(issueId);
    } else {
      newSelected.add(issueId);
    }
    setSelectedIssues(newSelected);
  };

  const selectAllIssues = () => {
    if (selectedIssues.size === issues.length) {
      setSelectedIssues(new Set());
    } else {
      setSelectedIssues(new Set(issues.map(i => i.id)));
    }
  };

  const exportToInvoice = (invoiceType) => {
    const selectedIssuesList = issues.filter(i => selectedIssues.has(i.id));
    
    if (selectedIssuesList.length === 0) {
      toast.error('Please select at least one issue');
      return;
    }

    const tasksForInvoice = selectedIssuesList.map((issue) => ({
      description_en: `${issue.id}: ${issue.subject}`,
      description_ua: '',
      description: `${issue.id}: ${issue.subject}`,
      hours: ''
    }));

    sessionStorage.setItem('redmine_tasks', JSON.stringify(tasksForInvoice));
    sessionStorage.setItem('redmine_month', String(selectedMonth - 1));
    sessionStorage.setItem('redmine_year', String(selectedYear));

    toast.success(`${selectedIssuesList.length} tasks ready to import`);
    navigate(invoiceType === 'ua' ? '/pocketbook-invoice' : '/pocketbook-wise');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <div className="space-y-6" data-testid="jira-tasks-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/templates')}
            className="h-9 w-9 p-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">Jira Tasks</h1>
            <p className="page-subtitle">
              Import tasks from Jira to generate invoices
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Filters */}
        <div className="space-y-6">
          {/* User Selection */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-[#0052CC]" />
                Select User
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="form-group">
                <Label>Jira User ({users.length} users)</Label>
                <SearchableSelect
                  options={users}
                  value={selectedUserId}
                  onValueChange={setSelectedUserId}
                  placeholder={loadingUsers ? "Loading users..." : "Search and select a user"}
                  searchPlaceholder="Search by name or email..."
                  emptyMessage="No users found"
                  disabled={loadingUsers}
                  displayKey="name"
                  valueKey="id"
                  secondaryKey="email"
                />
              </div>
              
              {/* Filter Type */}
              <div className="form-group">
                <Label className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filter By
                </Label>
                <Select 
                  value={filterType} 
                  onValueChange={setFilterType}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="assigned">Issues Assigned To User</SelectItem>
                    <SelectItem value="reporter">Issues Reported By User</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {filterType === 'assigned' 
                    ? 'Tasks the user is responsible for' 
                    : 'Tasks the user created'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Period Selection */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#0052CC]" />
                Select Period
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="form-grid">
                <div className="form-group">
                  <Label>Month</Label>
                  <Select 
                    value={String(selectedMonth)} 
                    onValueChange={(v) => setSelectedMonth(parseInt(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((month) => (
                        <SelectItem key={month.value} value={String(month.value)}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <Label>Year</Label>
                  <Select 
                    value={String(selectedYear)} 
                    onValueChange={(v) => setSelectedYear(parseInt(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {YEARS.map((year) => (
                        <SelectItem key={year} value={String(year)}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                onClick={fetchIssues} 
                disabled={!selectedUserId || loadingIssues}
                className="w-full bg-[#0052CC] hover:bg-[#0747A6]"
              >
                {loadingIssues ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Load Tasks
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Export Actions */}
          {issues.length > 0 && (
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#0052CC]" />
                  Export to Invoice
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-600">
                  Selected: {selectedIssues.size} of {issues.length} tasks
                </p>
                <Button 
                  onClick={() => exportToInvoice('ua')}
                  disabled={selectedIssues.size === 0}
                  className="w-full bg-[#0052CC] hover:bg-[#0747A6]"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Export to Pocketbook UA
                </Button>
                <Button 
                  onClick={() => exportToInvoice('wise')}
                  disabled={selectedIssues.size === 0}
                  variant="outline"
                  className="w-full"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Export to Pocketbook Wise
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Issues List */}
        <div className="lg:col-span-2">
          <Card className="dashboard-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-[#0052CC]" />
                Tasks / Issues
              </CardTitle>
              {issues.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={translateToEnglish}
                    disabled={translating}
                  >
                    {translating ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Languages className="w-4 h-4 mr-1" />
                    )}
                    Translate to English
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={selectAllIssues}
                  >
                    {selectedIssues.size === issues.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {loadingIssues ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#0052CC]" />
                </div>
              ) : issues.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No tasks loaded yet</p>
                  <p className="text-sm mt-1">Select a user and period, then click "Load Tasks"</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {issues.map((issue) => (
                    <div 
                      key={issue.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedIssues.has(issue.id) 
                          ? 'bg-[#0052CC]/10 border-[#0052CC]' 
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                      onClick={() => toggleIssueSelection(issue.id)}
                    >
                      <Checkbox 
                        checked={selectedIssues.has(issue.id)}
                        onCheckedChange={() => toggleIssueSelection(issue.id)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-[#0052CC] font-semibold">
                            {issue.id}
                          </span>
                          <a 
                            href={`https://pocketbook-jira.atlassian.net/browse/${issue.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-slate-400 hover:text-[#0052CC]"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        <p className="text-sm text-slate-900 mt-1">{issue.subject}</p>
                        <div className="flex items-center flex-wrap gap-2 mt-2 text-xs text-slate-500">
                          {issue.project && (
                            <span className="bg-slate-100 px-2 py-0.5 rounded">
                              {issue.project}
                            </span>
                          )}
                          {issue.status && (
                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              {issue.status}
                            </span>
                          )}
                          <span>{formatDate(issue.updated_on || issue.created_on)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
